import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Tooltip,
  Paper,
  Avatar,
  Link as MuiLink,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VerifiedUser as VerifiedIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Description as DescriptionIcon,
  Comment as CommentIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { User, Document, AdminComment, ProfileApprovalRequest } from '../../types';
import { formatDateTimeIst } from '../../utils/dateUtils';
import AdminDataDeletionTab from '../../components/common/AdminDataDeletionTab';

const PROFILE_FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  last_name: 'Last Name',
  phone: 'Phone',
  date_of_birth: 'Date of Birth',
  gender: 'Gender',
  address: 'Address',
  city: 'City',
  state: 'State',
  pincode: 'Pincode',
  country: 'Country',
  bio: 'Bio',
  specialization: 'Specialization',
  experience_years: 'Experience Years',
  license_number: 'License Number',
  license_expiry: 'License Expiry',
};

const FIELD_UPDATE_GROUPS = {
  email: {
    label: 'Email',
    targets: ['email', 'contact_email'],
  },
  phone: {
    label: 'Phone',
    targets: ['phone', 'contact_phone'],
  },
} as const;

const FIELD_UPDATE_GROUP_LOOKUP: Record<string, keyof typeof FIELD_UPDATE_GROUPS | undefined> = {};
Object.entries(FIELD_UPDATE_GROUPS).forEach(([groupKey, group]) => {
  group.targets.forEach((target) => {
    FIELD_UPDATE_GROUP_LOOKUP[target] = groupKey as keyof typeof FIELD_UPDATE_GROUPS;
  });
});

const toTitleCase = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const groupFieldUpdateItems = (items: any[] = []) => {
  const map = new Map<
    string,
    {
      label: string;
      items: any[];
    }
  >();

  items.forEach((item) => {
    const groupKey = FIELD_UPDATE_GROUP_LOOKUP[item.field_name];
    const mapKey = groupKey || item.field_name;
    const label = groupKey ? FIELD_UPDATE_GROUPS[groupKey].label : toTitleCase(item.field_name);

    if (!map.has(mapKey)) {
      map.set(mapKey, { label, items: [item] });
    } else {
      map.get(mapKey)!.items.push(item);
    }
  });

  return Array.from(map.values()).map((group) => {
    const orderedItems = group.items;
    const displayCurrentValue =
      orderedItems.find((item) => item.current_value)?.current_value ||
      orderedItems[0]?.current_value ||
      '';
    const extraFields = orderedItems
      .slice(1)
      .map((item) => toTitleCase(item.field_name))
      .filter((label) => !!label);

    return {
      label: group.label,
      primary: orderedItems[0],
      displayCurrentValue,
      extraFields,
    };
  });
};

const countPendingFieldUpdateItems = (requests: any[] = []) =>
  requests.reduce((total, request) => {
    const pendingItems = (request.items || []).filter((item: any) => item.status === 'pending');
    return total + pendingItems.length;
  }, 0);

const getProfileFieldLabel = (fieldName: string): string =>
  PROFILE_FIELD_LABELS[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

const getApprovalStatusColor = (status: string): 'default' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  if (!status) return '';
  return status.charAt(0).toUpperCase() + status.slice(1);
};


const ProfessionalsPage: React.FC = () => {
  const [professionals, setProfessionals] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [verificationFilter, setVerificationFilter] = useState<string>('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
  });
  const [selectedProfessional, setSelectedProfessional] = useState<User | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [statistics, setStatistics] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [comments, setComments] = useState<AdminComment[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState<'verification' | 'general' | 'document' | 'service'>('general');
  const [settingsCompleteness, setSettingsCompleteness] = useState<any>(null);
  const [approvalRequests, setApprovalRequests] = useState<ProfileApprovalRequest[]>([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [approvalActionLoadingId, setApprovalActionLoadingId] = useState<number | null>(null);
  const [approvalRejectDialogOpen, setApprovalRejectDialogOpen] = useState(false);
  const [approvalRejectionReason, setApprovalRejectionReason] = useState('');
  const [approvalRequestToReject, setApprovalRequestToReject] = useState<ProfileApprovalRequest | null>(null);
  const [fieldUpdateRequests, setFieldUpdateRequests] = useState<any[]>([]);
  const pendingFieldUpdateItemCount = useMemo(
    () => countPendingFieldUpdateItems(fieldUpdateRequests),
    [fieldUpdateRequests]
  );
  const [fieldUpdateLoading, setFieldUpdateLoading] = useState(false);
  const [fieldUpdateError, setFieldUpdateError] = useState<string | null>(null);
  const [fieldUpdateItemActionLoading, setFieldUpdateItemActionLoading] = useState<number | null>(null);
  const [fieldUpdateRejectDialogOpen, setFieldUpdateRejectDialogOpen] = useState(false);
  const [fieldUpdateRejectionReason, setFieldUpdateRejectionReason] = useState('');
  const [fieldUpdateItemToReject, setFieldUpdateItemToReject] = useState<any | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [exceptionDialogOpen, setExceptionDialogOpen] = useState(false);
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionExpiresAt, setExceptionExpiresAt] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [documentActionLoading, setDocumentActionLoading] = useState<number | null>(null);
  const [unverifyDialogOpen, setUnverifyDialogOpen] = useState(false);
  const [unverifyComment, setUnverifyComment] = useState('');
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [deactivateComment, setDeactivateComment] = useState('');
  const fieldUpdateLoadSeqRef = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      
      // Build filter parameters
      const params: any = {
        user_type: 'professional',
        per_page: pagination.pageSize,
        page: pagination.page + 1,
        search: searchQuery || undefined,
      };
      
      // Add verification filter if not 'all'
      if (verificationFilter !== 'all') {
        params.is_verified = verificationFilter === 'verified';
      }
      
      // Add account status filter if not 'all'
      if (accountStatusFilter !== 'all') {
        params.is_active = accountStatusFilter === 'active';
      }
      
      const response = await apiService.getUsers(params);
      setProfessionals(response.data || []);
      setPagination(prev => ({ ...prev, total: response.pagination?.total || 0 }));
    } catch (err: any) {
      console.error('Error fetching professionals:', err);

      // Handle rate limit errors with user-friendly message
      if (err.status === 429 || err.response?.status === 429) {
        const retryAfter = err.retryAfter || err.response?.headers?.['retry-after'] || 60;
        setError(`Too many requests. Please wait ${retryAfter} seconds before trying again.`);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load professionals');
      }
      setProfessionals([]);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, verificationFilter, accountStatusFilter, pagination.page, pagination.pageSize]);

  // Fetch statistics only on mount
  useEffect(() => {
    fetchStatistics();
  }, []);

  // Reset to the first page whenever the search query or filters change,
  // so a stale page index from a previous, larger result set can't produce an empty grid.
  useEffect(() => {
    setPagination(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [searchQuery, verificationFilter, accountStatusFilter]);

  // Fetch data on mount and whenever search/filters/pagination change (debounced so
  // typing in the search box doesn't fire a request per keystroke)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchData]);

  const fetchStatistics = async () => {
    try {
      const stats = await apiService.getUserStatistics();
      setStatistics(stats);
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
      // Don't show error for rate limit - it will be handled by the API interceptor
      // Only set error state for other errors if needed
      if (err.status !== 429) {
        // Handle other errors silently or show a less intrusive message
      }
    }
  };

  const loadDocuments = useCallback(async (userId: number) => {
    try {
      setDocumentsLoading(true);
      const docs = await apiService.getDocumentsByUser(userId);
      console.log('Loaded documents for user', userId, ':', docs);
      setDocuments(docs || []);
    } catch (err: any) {
      console.error('Error loading documents:', err);
      console.error('Error details:', err.response?.data);
      setDocuments([]);
      // Show error to user
      if (err.response?.status !== 401) {
        setError(err.response?.data?.message || 'Failed to load documents');
      }
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const loadComments = useCallback(async (professionalId: number) => {
    try {
      setCommentsLoading(true);
      const comms = await apiService.getAdminComments(professionalId);
      setComments(comms);
    } catch (err: any) {
      console.error('Error loading comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  }, []);

  const loadSettingsCompleteness = useCallback(async (userId: number) => {
    try {
      setSettingsLoading(true);
      const completeness = await apiService.getSettingsCompleteness(userId);
      setSettingsCompleteness(completeness);
    } catch (err: any) {
      console.error('Error loading settings completeness:', err);
      setSettingsCompleteness(null);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  
  const loadApprovalRequests = useCallback(async () => {
    const profileId = selectedProfessional?.profile?.id;
    if (!profileId) {
      setApprovalRequests([]);
      return;
    }

    try {
      setApprovalLoading(true);
      setApprovalError(null);
      const response = await apiService.getAdminProfileApprovalRequests({
        profile_id: profileId,
        per_page: 50,
      });
      setApprovalRequests(response.data);
    } catch (err: any) {
      console.error('Error loading approval requests:', err);
      setApprovalError(err.response?.data?.message || 'Failed to load approval requests');
      setApprovalRequests([]);
    } finally {
      setApprovalLoading(false);
    }
  }, [selectedProfessional?.profile?.id]);

  const refreshSelectedProfessional = useCallback(async () => {
    if (!selectedProfessional?.id) return;
    try {
      const updated = await apiService.getUser(selectedProfessional.id);
      setSelectedProfessional(updated);
      setProfessionals((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err: any) {
      console.error('Error refreshing professional:', err);
    }
  }, [selectedProfessional?.id]);

  const handleApproveRequest = useCallback(
    async (request: ProfileApprovalRequest) => {
      setApprovalActionLoadingId(request.id);
      try {
        await apiService.approveProfileApprovalRequest(request.id);
        alert('Approval request approved successfully');
        await refreshSelectedProfessional();
        await loadApprovalRequests();
      } catch (err: any) {
        console.error('Error approving approval request:', err);
        alert(err.response?.data?.message || 'Failed to approve approval request');
      } finally {
        setApprovalActionLoadingId(null);
      }
    },
    [loadApprovalRequests, refreshSelectedProfessional]
  );

  const openApprovalRejectDialog = useCallback((request: ProfileApprovalRequest) => {
    setApprovalRequestToReject(request);
    setApprovalRejectionReason('');
    setApprovalRejectDialogOpen(true);
  }, []);

  const closeApprovalRejectDialog = useCallback(() => {
    setApprovalRejectDialogOpen(false);
    setApprovalRequestToReject(null);
    setApprovalRejectionReason('');
  }, []);

  const handleRejectRequest = useCallback(async () => {
    if (!approvalRequestToReject) return;

    const reason = approvalRejectionReason.trim();
    if (!reason) {
      alert('Please provide a rejection reason.');
      return;
    }

    setApprovalActionLoadingId(approvalRequestToReject.id);
    try {
      await apiService.rejectProfileApprovalRequest(approvalRequestToReject.id, reason);
      alert('Approval request rejected successfully');
      closeApprovalRejectDialog();
      await refreshSelectedProfessional();
      await loadApprovalRequests();
    } catch (err: any) {
      console.error('Error rejecting approval request:', err);
      alert(err.response?.data?.message || 'Failed to reject approval request');
    } finally {
      setApprovalActionLoadingId(null);
    }
  }, [approvalRejectionReason, approvalRequestToReject, closeApprovalRejectDialog, loadApprovalRequests, refreshSelectedProfessional]);

  const loadFieldUpdateRequests = useCallback(async () => {
    const profileId = selectedProfessional?.profile?.id;
    if (!profileId) {
      fieldUpdateLoadSeqRef.current += 1;
      setFieldUpdateRequests([]);
      return;
    }

    const requestSeq = ++fieldUpdateLoadSeqRef.current;

    try {
      setFieldUpdateLoading(true);
      setFieldUpdateError(null);
      const response = await apiService.getAdminFieldUpdateRequests({
        profile_id: profileId,
        per_page: 50,
      });
      if (requestSeq !== fieldUpdateLoadSeqRef.current) return;
      setFieldUpdateRequests(response.data);
    } catch (err: any) {
      if (requestSeq !== fieldUpdateLoadSeqRef.current) return;
      console.error('Error loading field update requests:', err);
      setFieldUpdateError(err.response?.data?.message || 'Failed to load field update requests');
      setFieldUpdateRequests([]);
    } finally {
      if (requestSeq !== fieldUpdateLoadSeqRef.current) return;
      setFieldUpdateLoading(false);
    }
  }, [selectedProfessional?.profile?.id]);

  const handleApproveFieldUpdateItem = useCallback(
    async (item: any) => {
      setFieldUpdateItemActionLoading(item.id);
      try {
        await apiService.approveFieldUpdateRequestItem(item.id);
        alert('Field approved successfully');
        await refreshSelectedProfessional();
        await loadFieldUpdateRequests();
      } catch (err: any) {
        console.error('Error approving field update item:', err);
        alert(err.response?.data?.message || 'Failed to approve field');
      } finally {
        setFieldUpdateItemActionLoading(null);
      }
    },
    [loadFieldUpdateRequests, refreshSelectedProfessional]
  );

  const openFieldUpdateRejectDialog = useCallback((item: any) => {
    setFieldUpdateItemToReject(item);
    setFieldUpdateRejectionReason('');
    setFieldUpdateRejectDialogOpen(true);
  }, []);

  const closeFieldUpdateRejectDialog = useCallback(() => {
    setFieldUpdateRejectDialogOpen(false);
    setFieldUpdateItemToReject(null);
    setFieldUpdateRejectionReason('');
  }, []);

  const handleRejectFieldUpdateItem = useCallback(async () => {
    if (!fieldUpdateItemToReject) return;

    const reason = fieldUpdateRejectionReason.trim();
    if (!reason) {
      alert('Please provide a rejection reason.');
      return;
    }

    setFieldUpdateItemActionLoading(fieldUpdateItemToReject.id);
    try {
      await apiService.rejectFieldUpdateRequestItem(fieldUpdateItemToReject.id, reason);
      alert('Field rejected successfully');
      closeFieldUpdateRejectDialog();
      await refreshSelectedProfessional();
      await loadFieldUpdateRequests();
    } catch (err: any) {
      console.error('Error rejecting field update item:', err);
      alert(err.response?.data?.message || 'Failed to reject field');
    } finally {
      setFieldUpdateItemActionLoading(null);
    }
  }, [fieldUpdateRejectionReason, fieldUpdateItemToReject, closeFieldUpdateRejectDialog, loadFieldUpdateRequests, refreshSelectedProfessional]);

  const handleViewDetails = useCallback(async (professional: User) => {
    setSelectedProfessional(professional);
    setOpenDialog(true);
    setDialogTab(0);
    // Load documents, comments, and settings completeness when dialog opens
    if (professional.id) {
      // Always load documents when dialog opens
      loadDocuments(professional.id);
      loadComments(professional.id);
      loadSettingsCompleteness(professional.id);
    }
  }, [loadComments, loadDocuments, loadSettingsCompleteness]);

  // If we navigated here from Leads, auto-open the user detail dialog.
  // Fetched directly by id rather than looked up in `professionals`, since with real
  // server-side pagination the target user may not be on the currently loaded page.
  useEffect(() => {
    const type = sessionStorage.getItem('admin_open_user_type');
    const idRaw = sessionStorage.getItem('admin_open_user_id');
    if (type !== 'professional' || !idRaw) return;

    const id = Number(idRaw);
    if (!Number.isFinite(id)) return;

    sessionStorage.removeItem('admin_open_user_type');
    sessionStorage.removeItem('admin_open_user_id');

    apiService.getUser(id)
      .then((user) => handleViewDetails(user))
      .catch((err) => console.error('Error opening professional from lead:', err));
  }, [handleViewDetails]);

  // Reload documents when switching to Documents tab (in case they weren't loaded initially)
  useEffect(() => {
    if (dialogTab === 1 && selectedProfessional?.id && documents.length === 0 && !documentsLoading) {
      loadDocuments(selectedProfessional.id);
    }
  }, [dialogTab, selectedProfessional?.id, documents.length, documentsLoading, loadDocuments]);

  useEffect(() => {
    if (dialogTab === 3) {
      loadApprovalRequests();
    }
  }, [dialogTab, loadApprovalRequests]);

  useEffect(() => {
    if (selectedProfessional?.profile?.id) {
      loadFieldUpdateRequests();
    } else {
      setFieldUpdateRequests([]);
    }
  }, [selectedProfessional?.profile?.id, loadFieldUpdateRequests]);

  useEffect(() => {
    if (dialogTab === 4) {
      loadFieldUpdateRequests();
    }
  }, [dialogTab, loadFieldUpdateRequests]);

  const handleCloseDialog = useCallback(() => {
    fieldUpdateLoadSeqRef.current += 1;
    setOpenDialog(false);
    setSelectedProfessional(null);
    setDialogTab(0);
    setDocuments([]);
    setComments([]);
    setNewComment('');
    setSettingsCompleteness(null);
  }, []);

  const handleProfessionalDeleted = useCallback(() => {
    handleCloseDialog();
    fetchData();
  }, [fetchData, handleCloseDialog]);

  const handleAddComment = async () => {
    if (!selectedProfessional || !newComment.trim()) return;
    
    try {
      const comment = await apiService.createAdminComment({
        professional_id: selectedProfessional.id,
        comment: newComment,
        comment_type: commentType,
      });
      setComments([comment, ...comments]);
      setNewComment('');
      alert('Comment added successfully');
    } catch (err: any) {
      console.error('Error adding comment:', err);
      alert(err.response?.data?.message || 'Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await apiService.deleteAdminComment(commentId);
      setComments(comments.filter(c => c.id !== commentId));
      alert('Comment deleted successfully');
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      alert(err.response?.data?.message || 'Failed to delete comment');
    }
  };

  const handleApproveDocument = async (document: Document) => {
    if (!window.confirm(`Approve "${document.document_name}"?`)) return;
    
    try {
      setDocumentActionLoading(document.id);
      const updated = await apiService.approveDocument(document.id);
      
      // Update document in list
      setDocuments(documents.map(d => d.id === document.id ? updated : d));
      alert('Document approved successfully');
    } catch (err: any) {
      console.error('Error approving document:', err);
      alert(err.response?.data?.message || 'Failed to approve document');
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const handleRejectDocument = async () => {
    if (!selectedDocument || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    try {
      setDocumentActionLoading(selectedDocument.id);
      const updated = await apiService.rejectDocument(selectedDocument.id, rejectionReason);
      
      // Update document in list
      setDocuments(documents.map(d => d.id === selectedDocument.id ? updated : d));
      
      setRejectDialogOpen(false);
      setSelectedDocument(null);
      setRejectionReason('');
      alert('Document rejected successfully');
    } catch (err: any) {
      console.error('Error rejecting document:', err);
      alert(err.response?.data?.message || 'Failed to reject document');
    } finally {
      setDocumentActionLoading(null);
    }
  };

  const openRejectDialog = (document: Document) => {
    setSelectedDocument(document);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleVerifyToggle = async (professional: User) => {
    // If trying to unverify, show confirmation dialog first
    if (professional.is_verified) {
      setSelectedProfessional(professional);
      setUnverifyComment('');
      setUnverifyDialogOpen(true);
      return;
    }
    
    // If trying to verify, check settings completeness first
    try {
      setActionLoading(professional.id);
      
      try {
        const completeness = await apiService.getSettingsCompleteness(professional.id);
        if (!completeness.complete) {
          // Show exception approval dialog instead
          // Set selectedProfessional so the dialog knows which professional to approve
          setSelectedProfessional(professional);
          setExceptionDialogOpen(true);
          setActionLoading(null);
          return;
        }
      } catch (err: any) {
        console.error('Error checking settings completeness:', err);
      }
      
      const updated = await apiService.updateUserVerification(professional.id, true);
      // Update the professional in the list
      setProfessionals(prev => prev.map(p => p.id === professional.id ? updated : p));
      // Update selected professional if dialog is open
      if (selectedProfessional?.id === professional.id) {
        setSelectedProfessional(updated);
      }
    } catch (err: any) {
      console.error('Error updating verification:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update verification status';
      alert(errorMessage);
      
      // If error mentions incomplete settings, show exception dialog
      if (errorMessage.includes('incomplete settings')) {
        // Set selectedProfessional so the dialog knows which professional to approve
        setSelectedProfessional(professional);
        setExceptionDialogOpen(true);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnverifyConfirm = async () => {
    if (!selectedProfessional) {
      alert('Error: Professional information is missing.');
      return;
    }
    
    const trimmedComment = unverifyComment?.trim() || '';
    if (!trimmedComment) {
      alert('Please provide a reason for unverifying this professional');
      return;
    }
    
    if (trimmedComment.length < 10) {
      alert('Comment must be at least 10 characters long. Please provide more details.');
      return;
    }

    try {
      setActionLoading(selectedProfessional.id);
      const updated = await apiService.updateUserVerification(selectedProfessional.id, false);
      // Update the professional in the list
      setProfessionals(prev => prev.map(p => p.id === selectedProfessional.id ? updated : p));
      // Update selected professional if dialog is open
      if (selectedProfessional?.id === updated.id) {
        setSelectedProfessional(updated);
      }
      
      setUnverifyDialogOpen(false);
      setUnverifyComment('');
      alert('Professional unverified successfully');
    } catch (err: any) {
      console.error('Error unverifying professional:', err);
      alert(err.response?.data?.message || 'Failed to unverify professional');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExceptionApproval = async () => {
    // Validate inputs with explicit checks
    if (!selectedProfessional) {
      alert('Error: Professional information is missing. Please close the dialog and try again.');
      console.error('selectedProfessional is null when trying to approve exception');
      return;
    }
    
    const trimmedReason = exceptionReason?.trim() || '';
    if (!trimmedReason) {
      alert('Please provide a reason for the exception');
      return;
    }
    
    // Backend requires reason to be at least 10 characters
    if (trimmedReason.length < 10) {
      alert('Reason must be at least 10 characters long. Please provide more details.');
      return;
    }
    
    if (trimmedReason.length > 500) {
      alert('Reason must be less than 500 characters.');
      return;
    }
    
    const trimmedExpiresAt = exceptionExpiresAt?.trim() || '';
    if (!trimmedExpiresAt) {
      alert('Please provide an expiry date and time');
      return;
    }

    try {
      setActionLoading(selectedProfessional.id);
      
      // Convert datetime-local format (YYYY-MM-DDTHH:mm) to Laravel-compatible format
      // The datetime-local input returns format like "2024-01-20T14:30"
      // Laravel's date validation with 'after:now' expects format like "2024-01-20 14:30:00" or ISO 8601
      // We'll convert to "YYYY-MM-DD HH:mm:ss" format which Laravel accepts
      let formattedExpiresAt = trimmedExpiresAt;
      
      // Convert from datetime-local format (YYYY-MM-DDTHH:mm) to Laravel format (YYYY-MM-DD HH:mm:ss)
      if (formattedExpiresAt.includes('T')) {
        const [datePart, timePart] = formattedExpiresAt.split('T');
        if (timePart) {
          // Add seconds if missing (datetime-local format is HH:mm, we need HH:mm:ss)
          const timeParts = timePart.split(':');
          if (timeParts.length === 2) {
            formattedExpiresAt = `${datePart} ${timePart}:00`;
          } else {
            formattedExpiresAt = `${datePart} ${timePart}`;
          }
        }
      }
      
      // Validate the date is in the future
      const expiryDate = new Date(formattedExpiresAt);
      if (isNaN(expiryDate.getTime())) {
        alert('Invalid date format. Please select a valid date and time.');
        setActionLoading(null);
        return;
      }
      
      // Add a small buffer (1 second) to ensure the date is definitely in the future
      const now = new Date();
      now.setSeconds(now.getSeconds() + 1);
      if (expiryDate <= now) {
        alert('Expiry date must be in the future. Please select a future date and time.');
        setActionLoading(null);
        return;
      }
      
      console.log('Sending exception approval:', {
        userId: selectedProfessional.id,
        reason: trimmedReason,
        reasonLength: trimmedReason.length,
        expiresAt: formattedExpiresAt,
        expiresAtDate: expiryDate.toISOString()
      });
      
      const updated = await apiService.approveExceptionVerification(
        selectedProfessional.id,
        trimmedReason,
        formattedExpiresAt
      );
      
      // Update the professional in the list
      setProfessionals(prev => prev.map(p => p.id === selectedProfessional.id ? updated : p));
      // Update selected professional if dialog is open
      if (selectedProfessional?.id === updated.id) {
        setSelectedProfessional(updated);
      }
      
      setExceptionDialogOpen(false);
      setExceptionReason('');
      setExceptionExpiresAt('');
      alert('Exception verification approved successfully');
    } catch (err: any) {
      console.error('Error approving exception verification:', err);
      console.error('Error response:', err.response?.data);
      
      // Show detailed error message from backend
      let errorMessage = 'Failed to approve exception verification';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      // If there are validation errors, show them
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const errorDetails = Object.entries(errors)
          .map(([field, messages]: [string, any]) => {
            const fieldName = field === 'reason' ? 'Reason' : field === 'expires_at' ? 'Expiry Date' : field;
            return `${fieldName}: ${Array.isArray(messages) ? messages.join(', ') : messages}`;
          })
          .join('\n');
        errorMessage = `${errorMessage}\n\n${errorDetails}`;
      }
      
      alert(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleActiveToggle = async (professional: User) => {
    // If trying to deactivate, show confirmation dialog first
    if (professional.is_active) {
      setSelectedProfessional(professional);
      setDeactivateComment('');
      setDeactivateDialogOpen(true);
      return;
    }
    
    // If activating, proceed directly
    try {
      setActionLoading(professional.id);
      const updated = await apiService.updateUserActiveStatus(professional.id, true);
      // Update the professional in the list
      setProfessionals(prev => prev.map(p => p.id === professional.id ? updated : p));
      // Update selected professional if dialog is open
      if (selectedProfessional?.id === professional.id) {
        setSelectedProfessional(updated);
      }
      alert('Professional activated successfully');
    } catch (err: any) {
      console.error('Error updating active status:', err);
      alert(err.response?.data?.message || 'Failed to update active status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!selectedProfessional) {
      alert('Error: Professional information is missing.');
      return;
    }
    
    const trimmedComment = deactivateComment?.trim() || '';
    if (!trimmedComment) {
      alert('Please provide a reason for deactivating this professional');
      return;
    }
    
    if (trimmedComment.length < 10) {
      alert('Comment must be at least 10 characters long. Please provide more details.');
      return;
    }

    try {
      setActionLoading(selectedProfessional.id);
      const updated = await apiService.updateUserActiveStatus(selectedProfessional.id, false);
      // Update the professional in the list
      setProfessionals(prev => prev.map(p => p.id === selectedProfessional.id ? updated : p));
      // Update selected professional if dialog is open
      if (selectedProfessional?.id === updated.id) {
        setSelectedProfessional(updated);
      }
      
      setDeactivateDialogOpen(false);
      setDeactivateComment('');
      alert('Professional deactivated successfully');
    } catch (err: any) {
      console.error('Error deactivating professional:', err);
      alert(err.response?.data?.message || 'Failed to deactivate professional');
    } finally {
      setActionLoading(null);
    }
  };

  const columns: GridColDef<User>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'professionalId',
      headerName: 'Professional Code',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.user_code ?? '—'}</Typography>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.row.name}</Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.row.email}</Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PhoneIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.row.phone || 'N/A'}</Typography>
        </Box>
      ),
    },
    {
      field: 'is_verified',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {params.row.is_verified ? (
            <Chip
              icon={<VerifiedIcon />}
              label="Verified"
              color="success"
              size="small"
            />
          ) : (
            <Chip
              label="Unverified"
              color="warning"
              size="small"
            />
          )}
          {!params.row.is_active && (
            <Chip
              icon={<BlockIcon />}
              label="Inactive"
              color="error"
              size="small"
            />
          )}
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Joined',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {new Date(params.row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 300,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details">
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleViewDetails(params.row)}
            >
              View Details
            </Button>
          </Tooltip>
          <Tooltip title={params.row.is_verified ? 'Unverify Professional' : 'Verify Professional'}>
            <Button
              size="small"
              variant={params.row.is_verified ? "outlined" : "contained"}
              color={params.row.is_verified ? "warning" : "success"}
              startIcon={params.row.is_verified ? <CancelIcon /> : <CheckCircleIcon />}
              onClick={() => handleVerifyToggle(params.row)}
              disabled={actionLoading === params.row.id}
            >
              {params.row.is_verified ? 'Unverify' : 'Verify'}
            </Button>
          </Tooltip>
          <Tooltip title={params.row.is_active ? 'Deactivate Professional' : 'Activate Professional'}>
            <Button
              size="small"
              variant={params.row.is_active ? "outlined" : "contained"}
              color={params.row.is_active ? "error" : "success"}
              startIcon={params.row.is_active ? <BlockIcon /> : <CheckCircleIcon />}
              onClick={() => handleActiveToggle(params.row)}
              disabled={actionLoading === params.row.id}
            >
              {params.row.is_active ? 'Deactivate' : 'Activate'}
            </Button>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Professionals Management
      </Typography>

      {statistics && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {statistics.total_professionals || pagination.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Professionals
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {statistics.verified_professionals || professionals.filter(p => p.is_verified).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verified
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {statistics.unverified_professionals || professionals.filter(p => !p.is_verified).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unverified
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {statistics.active_professionals || professionals.filter(p => p.is_active).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            placeholder="Search professionals by name, email, phone, or professional code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Verification Status</InputLabel>
                <Select
                  value={verificationFilter}
                  label="Verification Status"
                  onChange={(e) => setVerificationFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="unverified">Unverified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Account Status</InputLabel>
                <Select
                  value={accountStatusFilter}
                  label="Account Status"
                  onChange={(e) => setAccountStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid<User>
              rows={professionals}
              columns={columns}
              paginationMode="server"
              paginationModel={{ page: pagination.page, pageSize: pagination.pageSize }}
              onPaginationModelChange={(model) =>
                setPagination(prev => ({ ...prev, page: model.page, pageSize: model.pageSize }))
              }
              rowCount={pagination.total}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{ height: 600 }}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Professional Details</Typography>
            {selectedProfessional && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {selectedProfessional.is_verified ? (
                  <Chip label="Verified" color="success" size="small" />
                ) : (
                  <Chip label="Unverified" color="warning" size="small" />
                )}
                {selectedProfessional.is_active ? (
                  <Chip label="Active" color="success" size="small" />
                ) : (
                  <Chip label="Inactive" color="error" size="small" />
                )}
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProfessional && (
            <Box sx={{ mt: 2 }}>
              <Tabs 
                value={dialogTab} 
                onChange={(e, newValue) => {
                  setDialogTab(newValue);
                  // Reload documents when switching to Documents tab
                  if (newValue === 1 && selectedProfessional?.id) {
                    loadDocuments(selectedProfessional.id);
                  }
                }} 
                sx={{ mb: 3 }}
              >
                <Tab label="Overview" />
                <Tab label={`Documents (${documents.length})`} />
                <Tab label={`Comments (${comments.length})`} />
                <Tab label={`Profile Requests (${approvalRequests.length})`} />
                <Tab label={`Field Updates (${pendingFieldUpdateItemCount})`} />
                <Tab label="Data Deletion" />
              </Tabs>

              {/* Overview Tab */}
              {dialogTab === 0 && (
                <Grid container spacing={2}>
                  {/* Incomplete Settings Alert */}
                  {settingsLoading ? (
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    </Grid>
                  ) : settingsCompleteness && !settingsCompleteness.complete && (
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Incomplete Settings Detected</strong>
                        </Typography>
                        <Typography variant="body2" component="div">
                          This professional has incomplete settings. Verification is blocked until settings are completed.
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" component="div">
                            <strong>Missing Items:</strong>
                          </Typography>
                          <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                            {settingsCompleteness.missing_items.map((item: string) => (
                              <li key={item}>
                                <Typography variant="body2">
                                  {item === 'documents' && 'No documents uploaded'}
                                  {item === 'documents_approval' && 'Documents pending approval'}
                                  {item === 'profile' && 'Profile information incomplete'}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                          {settingsCompleteness.details.documents && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {settingsCompleteness.details.documents.message}
                                {settingsCompleteness.details.documents.missing_fields && (
                                  <span>: {settingsCompleteness.details.documents.missing_fields.join(', ')}</span>
                                )}
                              </Typography>
                            </Box>
                          )}
                          {settingsCompleteness.details.profile && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                {settingsCompleteness.details.profile.message}
                                {settingsCompleteness.details.profile.missing_fields && (
                                  <span>: {settingsCompleteness.details.profile.missing_fields.join(', ')}</span>
                                )}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Alert>
                    </Grid>
                  )}
                  
                  {/* Exception Verification Info */}
                  {selectedProfessional.is_exception_verification && (
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          <strong>Exception Verification Active</strong>
                        </Typography>
                        <Typography variant="body2">
                          <strong>Reason:</strong> {selectedProfessional.exception_reason || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Expires:</strong> {selectedProfessional.exception_expires_at 
                            ? formatDateTimeIst(selectedProfessional.exception_expires_at)
                            : 'N/A'}
                        </Typography>
                        {selectedProfessional.exception_expires_at && 
                         new Date(selectedProfessional.exception_expires_at) < new Date() && (
                          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            <strong>⚠️ Exception has expired. Professional should be unverified.</strong>
                          </Typography>
                        )}
                      </Alert>
                    </Grid>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Name
                    </Typography>
                    <Typography variant="body1">{selectedProfessional.name}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{selectedProfessional.email}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">{selectedProfessional.phone || 'N/A'}</Typography>
                  </Grid>
                  {selectedProfessional.profile && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          City
                        </Typography>
                        <Typography variant="body1">
                          {selectedProfessional.profile.city || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          State
                        </Typography>
                        <Typography variant="body1">
                          {selectedProfessional.profile.state || 'N/A'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Joined Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTimeIst(selectedProfessional.created_at)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        component={Link}
                        to={`/services?professional_id=${selectedProfessional.id}`}
                        variant="outlined"
                        color="primary"
                        onClick={handleCloseDialog}
                      >
                        View Professional Services
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {/* Documents Tab */}
              {dialogTab === 1 && (
                <Box>
                  {documentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : documents.length === 0 ? (
                    <Alert severity="info">No documents uploaded by this professional.</Alert>
                  ) : (
                    <List>
                      {documents.map((doc, index) => (
                        <React.Fragment key={doc.id}>
                          <ListItem>
                            <ListItemAvatar>
                              <Avatar>
                                <DescriptionIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body1">{doc.document_name}</Typography>
                                  <Chip
                                    label={doc.status}
                                    size="small"
                                    color={
                                      doc.status === 'approved'
                                        ? 'success'
                                        : doc.status === 'rejected'
                                        ? 'error'
                                        : doc.status === 'pending'
                                        ? 'warning'
                                        : 'default'
                                    }
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="text.secondary">
                                    Type: {doc.document_type} | Size: {doc.file_size_human || `${(doc.file_size / 1024).toFixed(2)} KB`}
                                  </Typography>
                                  {doc.rejection_reason && (
                                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                      Rejection Reason: {doc.rejection_reason}
                                    </Typography>
                                  )}
                                  {doc.expiry_date && (
                                    <Typography variant="body2" color="text.secondary">
                                      Expires: {new Date(doc.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                edge="end"
                                component={MuiLink}
                                href={doc.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                  size="small"
                                  title="View Document"
                              >
                                <VisibilityIcon />
                              </IconButton>
                                {doc.status === 'pending' && (
                                  <>
                                    <IconButton
                                      edge="end"
                                      color="success"
                                      onClick={() => handleApproveDocument(doc)}
                                      disabled={documentActionLoading === doc.id}
                                      size="small"
                                      title="Approve Document"
                                    >
                                      <CheckCircleIcon />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      color="error"
                                      onClick={() => openRejectDialog(doc)}
                                      disabled={documentActionLoading === doc.id}
                                      size="small"
                                      title="Reject Document"
                                    >
                                      <CancelIcon />
                                    </IconButton>
                                  </>
                                )}
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < documents.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Comments Tab */}
              {dialogTab === 2 && (
                <Box>
                  {/* Add Comment Section */}
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Add Comment
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Comment Type</InputLabel>
                      <Select
                        value={commentType}
                        onChange={(e) => setCommentType(e.target.value as any)}
                        label="Comment Type"
                      >
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="verification">Verification</MenuItem>
                        <MenuItem value="document">Document</MenuItem>
                        <MenuItem value="service">Service</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Enter your comment or feedback for the professional..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                    >
                      Add Comment
                    </Button>
                  </Paper>

                  {/* Comments List */}
                  {commentsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : comments.length === 0 ? (
                    <Alert severity="info">No comments yet. Add a comment to communicate with this professional.</Alert>
                  ) : (
                    <List>
                      {comments.map((comment, index) => {
                        const isAutoUnverification = comment.comment_type === 'verification' && 
                          comment.comment && 
                          comment.comment.includes('Auto-unverified:');
                        
                        return (
                        <React.Fragment key={comment.id}>
                          <ListItem
                            sx={isAutoUnverification ? {
                              bgcolor: 'warning.light',
                              borderRadius: 1,
                              mb: 1,
                              borderLeft: '4px solid',
                              borderColor: 'warning.main',
                            } : {}}
                          >
                            <ListItemAvatar>
                              <Avatar sx={isAutoUnverification ? { bgcolor: 'warning.main' } : {}}>
                                <CommentIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <Typography variant="body1" fontWeight="medium">
                                    {isAutoUnverification ? 'System' : (comment.admin?.name || 'Admin')}
                                  </Typography>
                                  <Chip 
                                    label={comment.comment_type} 
                                    size="small"
                                    color={isAutoUnverification ? 'warning' : 'default'}
                                  />
                                  {isAutoUnverification && (
                                    <Chip label="Auto-Unverified" size="small" color="error" />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTimeIst(comment.created_at)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      mt: 1, 
                                      whiteSpace: 'pre-wrap',
                                      fontWeight: isAutoUnverification ? 'medium' : 'normal'
                                    }}
                                  >
                                    {comment.comment}
                                  </Typography>
                                  {comment.reply && (
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'success.light',
                                        borderRadius: 1,
                                        borderLeft: '3px solid',
                                        borderColor: 'success.main',
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        Professional Reply ({formatDateTimeIst(comment.replied_at!)})
                                      </Typography>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {comment.reply}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => handleDeleteComment(comment.id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < comments.length - 1 && <Divider />}
                        </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </Box>
              )}
              {dialogTab === 3 && (
                <Box>
                  {approvalLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : approvalError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {approvalError}
                    </Alert>
                  ) : approvalRequests.length === 0 ? (
                    <Alert severity="info">No profile approval requests found for this professional.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {approvalRequests.map((request) => (
                        <Card key={request.id} variant="outlined">
                          <CardContent>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight="medium">
                                {getProfileFieldLabel(request.field_name)}
                              </Typography>
                              <Chip
                                label={getStatusLabel(request.status)}
                                size="small"
                                color={getApprovalStatusColor(request.status)}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Requested on {formatDateTimeIst(request.created_at)}
                            </Typography>

                            <Box
                              sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                  xs: '1fr',
                                  sm: 'repeat(2, minmax(0, 1fr))',
                                },
                                mt: 2,
                              }}
                            >
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Current Value
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    bgcolor: 'action.disabledBackground',
                                    p: 1,
                                    borderRadius: 1,
                                  }}
                                >
                                  {request.current_value || '(empty)'}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Requested Value
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    bgcolor: 'action.selected',
                                    p: 1,
                                    borderRadius: 1,
                                  }}
                                >
                                  {request.requested_value}
                                </Typography>
                              </Box>
                            </Box>

                            {request.reason && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                  Professional's Reason
                                </Typography>
                                <Typography variant="body2">{request.reason}</Typography>
                              </Box>
                            )}

                            {request.status !== 'pending' && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                                {request.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                                {request.reviewer?.name || 'Admin'} on {formatDateTimeIst(request.reviewed_at)}
                              </Typography>
                            )}

                            {request.status === 'rejected' && request.rejection_reason && (
                              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                Rejection reason: {request.rejection_reason}
                              </Typography>
                            )}

                            {request.status === 'pending' && (
                              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleApproveRequest(request)}
                                  disabled={approvalActionLoadingId === request.id}
                                  startIcon={<CheckCircleIcon />}
                                >
                                  {approvalActionLoadingId === request.id ? 'Approving...' : 'Approve'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => openApprovalRejectDialog(request)}
                                  disabled={approvalActionLoadingId === request.id}
                                  startIcon={<CancelIcon />}
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}

              {/* Field Update Requests Tab */}
              {dialogTab === 4 && (
                <Box>
                  {fieldUpdateLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : fieldUpdateError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {fieldUpdateError}
                    </Alert>
                  ) : fieldUpdateRequests.length === 0 ? (
                    <Alert severity="info">No field update requests found for this professional.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {fieldUpdateRequests.map((request) => (
                        <Card key={request.id} variant="outlined">
                          <CardContent>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                                mb: 2,
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight="medium">
                                Request #{request.id}
                              </Typography>
                              <Chip
                                label={request.status.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                size="small"
                                color={
                                  request.status === 'approved'
                                    ? 'success'
                                    : request.status === 'rejected'
                                    ? 'error'
                                    : request.status === 'partially_approved'
                                    ? 'warning'
                                    : 'default'
                                }
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                              Requested on {formatDateTimeIst(request.created_at)}
                              {request.reason && ` • Reason: ${request.reason}`}
                            </Typography>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                              Fields in this request:
                            </Typography>

                            <Stack spacing={2}>
                              {groupFieldUpdateItems(request.items).map((group) => (
                                <Box
                                  key={`${request.id}-${group.label}`}
                                  sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor:
                                      group.primary.status === 'approved'
                                        ? 'success.light'
                                        : group.primary.status === 'rejected'
                                        ? 'error.light'
                                        : 'background.paper',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      flexWrap: 'wrap',
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Box>
                                    <Typography variant="subtitle2" fontWeight="medium">
                                        {group.label}
                                    </Typography>
                                      {group.extraFields.length > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                          Includes {group.extraFields.join(', ')}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Chip
                                      label={
                                        group.primary.status.charAt(0).toUpperCase() +
                                        group.primary.status.slice(1)
                                      }
                                      size="small"
                                      color={
                                        group.primary.status === 'approved'
                                          ? 'success'
                                          : group.primary.status === 'rejected'
                                          ? 'error'
                                          : 'warning'
                                      }
                                    />
                                  </Box>

                                  <Box
                                    sx={{
                                      display: 'grid',
                                      gap: 2,
                                      gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                      },
                                      mt: 1,
                                    }}
                                  >
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Current Value
                                      </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ mt: 0.5, p: 1, bgcolor: 'action.disabledBackground', borderRadius: 1 }}
                                  >
                                    {group.displayCurrentValue || '(empty)'}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Requested Value
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 0.5, p: 1, bgcolor: 'action.selected', borderRadius: 1 }}
                                      >
                                        {group.primary.requested_value}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  {group.primary.status === 'rejected' && group.primary.rejection_reason && (
                                    <Alert severity="error" sx={{ mt: 1 }}>
                                      <Typography variant="caption" fontWeight="medium">
                                        Rejection Reason:
                                      </Typography>
                                      <Typography variant="body2">{group.primary.rejection_reason}</Typography>
                                    </Alert>
                                  )}

                                  {group.primary.status === 'pending' && (
                                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={() => handleApproveFieldUpdateItem(group.primary)}
                                        disabled={fieldUpdateItemActionLoading === group.primary.id}
                                        startIcon={<CheckCircleIcon />}
                                      >
                                        {fieldUpdateItemActionLoading === group.primary.id ? 'Approving...' : 'Approve'}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => openFieldUpdateRejectDialog(group.primary)}
                                        disabled={fieldUpdateItemActionLoading === group.primary.id}
                                        startIcon={<CancelIcon />}
                                      >
                                        Reject
                                      </Button>
                                    </Box>
                                  )}

                                  {group.primary.status !== 'pending' && group.primary.reviewed_at && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                      {group.primary.status === 'approved' ? 'Approved' : 'Rejected'} by{' '}
                                      {group.primary.reviewer?.name || 'Admin'} on {formatDateTimeIst(group.primary.reviewed_at)}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
              {dialogTab === 5 && selectedProfessional && (
                <AdminDataDeletionTab user={selectedProfessional} onDeleted={handleProfessionalDeleted} />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedProfessional && (
            <>
              <Button
                variant={selectedProfessional.is_verified ? "outlined" : "contained"}
                color={selectedProfessional.is_verified ? "warning" : "success"}
                startIcon={selectedProfessional.is_verified ? <CancelIcon /> : <CheckCircleIcon />}
                onClick={() => {
                  handleVerifyToggle(selectedProfessional);
                }}
                disabled={actionLoading === selectedProfessional.id}
              >
                {selectedProfessional.is_verified ? 'Unverify' : 'Verify'}
              </Button>
              <Button
                variant={selectedProfessional.is_active ? "outlined" : "contained"}
                color={selectedProfessional.is_active ? "error" : "success"}
                startIcon={selectedProfessional.is_active ? <StopIcon /> : <PlayArrowIcon />}
                onClick={() => {
                  handleActiveToggle(selectedProfessional);
                }}
                disabled={actionLoading === selectedProfessional.id}
              >
                {selectedProfessional.is_active ? 'Deactivate' : 'Activate'}
              </Button>
            </>
          )}
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Request Rejection Dialog */}
      <Dialog open={approvalRejectDialogOpen} onClose={closeApprovalRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Profile Update Request</DialogTitle>
        <DialogContent>
          {approvalRequestToReject && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to reject the request to update <strong>{getProfileFieldLabel(approvalRequestToReject.field_name)}</strong>.
              </Alert>
              <TextField
                fullWidth
                label="Rejection Reason"
                multiline
                rows={4}
                value={approvalRejectionReason}
                onChange={(e) => setApprovalRejectionReason(e.target.value)}
                placeholder="Explain why this change cannot be approved."
                required
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This reason will be shared with the professional. Provide enough detail so they can improve their request.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalRejectDialog} disabled={approvalActionLoadingId === approvalRequestToReject?.id}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectRequest}
            variant="contained"
            color="error"
            disabled={!approvalRejectionReason.trim() || approvalActionLoadingId === approvalRequestToReject?.id}
          >
            {approvalActionLoadingId === approvalRequestToReject?.id ? 'Rejecting...' : 'Reject Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Update Item Rejection Dialog */}
      <Dialog open={fieldUpdateRejectDialogOpen} onClose={closeFieldUpdateRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Field Update</DialogTitle>
        <DialogContent>
          {fieldUpdateItemToReject && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to reject the request to update <strong>{getProfileFieldLabel(fieldUpdateItemToReject.field_name)}</strong>.
              </Alert>
              <TextField
                fullWidth
                label="Rejection Reason"
                multiline
                rows={4}
                value={fieldUpdateRejectionReason}
                onChange={(e) => setFieldUpdateRejectionReason(e.target.value)}
                placeholder="Explain why this change cannot be approved."
                required
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This reason will be shared with the professional. Provide enough detail so they can improve their request.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFieldUpdateRejectDialog} disabled={fieldUpdateItemActionLoading === fieldUpdateItemToReject?.id}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectFieldUpdateItem}
            variant="contained"
            color="error"
            disabled={!fieldUpdateRejectionReason.trim() || fieldUpdateItemActionLoading === fieldUpdateItemToReject?.id}
          >
            {fieldUpdateItemActionLoading === fieldUpdateItemToReject?.id ? 'Rejecting...' : 'Reject Field'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => {
        setRejectDialogOpen(false);
        setSelectedDocument(null);
        setRejectionReason('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Document</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to reject: <strong>{selectedDocument.document_name}</strong>
              </Alert>
              <TextField
                fullWidth
                label="Rejection Reason"
                multiline
                rows={4}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a clear reason for rejection (e.g., Document is unclear, Wrong document type, Expired document, etc.)"
                required
                sx={{ mt: 2 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This reason will be visible to the professional and they will need to upload a new document.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRejectDialogOpen(false);
            setSelectedDocument(null);
            setRejectionReason('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectDocument}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim() || documentActionLoading === selectedDocument?.id}
          >
            {documentActionLoading === selectedDocument?.id ? 'Rejecting...' : 'Reject Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exception Approval Dialog */}
      <Dialog open={exceptionDialogOpen} onClose={() => setExceptionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Exception Verification</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {selectedProfessional ? (
              <>
                <strong>{selectedProfessional.name}</strong> has incomplete settings. 
                You can approve verification as an exception for emergency cases only.
              </>
            ) : (
              'This professional has incomplete settings. You can approve verification as an exception for emergency cases only.'
            )}
          </Alert>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Reason for Exception"
              multiline
              rows={4}
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              placeholder="Please provide a detailed reason for approving this exception (e.g., Emergency case - urgent patient care needed)"
              required
              helperText={`Minimum 10 characters, maximum 500 characters. (${exceptionReason?.length || 0}/500)`}
              error={exceptionReason ? (exceptionReason.length < 10 || exceptionReason.length > 500) : false}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Expiry Date & Time"
              type="datetime-local"
              value={exceptionExpiresAt}
              onChange={(e) => setExceptionExpiresAt(e.target.value)}
              required
              InputLabelProps={{
                shrink: true,
              }}
              helperText="After this date, if settings are still incomplete, the professional will be automatically unverified"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExceptionDialogOpen(false);
            setExceptionReason('');
            setExceptionExpiresAt('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleExceptionApproval}
            variant="contained"
            color="warning"
            disabled={
              !exceptionReason?.trim() || 
              !exceptionExpiresAt?.trim() || 
              actionLoading === selectedProfessional?.id
            }
          >
            Approve Exception
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unverify Confirmation Dialog */}
      <Dialog open={unverifyDialogOpen} onClose={() => setUnverifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Unverify Professional</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are about to unverify: <strong>{selectedProfessional?.name}</strong>
            <br />
            <small>This will remove their verified status and they will need to be verified again.</small>
          </Alert>
          <TextField
            fullWidth
            label="Reason for Unverification *"
            multiline
            rows={4}
            value={unverifyComment}
            onChange={(e) => setUnverifyComment(e.target.value)}
            placeholder="Please provide a detailed reason for unverifying this professional (e.g., Violation of guidelines, Incomplete documentation, Suspicious activity, etc.)"
            required
            helperText={`Minimum 10 characters. (${unverifyComment?.length || 0}/500)`}
            error={unverifyComment ? (unverifyComment.length < 10 || unverifyComment.length > 500) : false}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUnverifyDialogOpen(false);
            setUnverifyComment('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleUnverifyConfirm}
            variant="contained"
            color="warning"
            disabled={
              !unverifyComment?.trim() || 
              unverifyComment.length < 10 ||
              actionLoading === selectedProfessional?.id
            }
          >
            Confirm Unverify
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deactivate Professional</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            You are about to deactivate: <strong>{selectedProfessional?.name}</strong>
            <br />
            <small>This will prevent them from accessing their account and their services will be hidden from patients.</small>
          </Alert>
          <TextField
            fullWidth
            label="Reason for Deactivation *"
            multiline
            rows={4}
            value={deactivateComment}
            onChange={(e) => setDeactivateComment(e.target.value)}
            placeholder="Please provide a detailed reason for deactivating this professional (e.g., Terms of service violation, Account suspension, Investigation required, etc.)"
            required
            helperText={`Minimum 10 characters. (${deactivateComment?.length || 0}/500)`}
            error={deactivateComment ? (deactivateComment.length < 10 || deactivateComment.length > 500) : false}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeactivateDialogOpen(false);
            setDeactivateComment('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeactivateConfirm}
            variant="contained"
            color="error"
            disabled={
              !deactivateComment?.trim() || 
              deactivateComment.length < 10 ||
              actionLoading === selectedProfessional?.id
            }
          >
            Confirm Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfessionalsPage;

